import React, { useState, useCallback, useEffect } from 'react';
import styles from './Solitaire.module.css';

type Suit = 'hearts' | 'diamonds' | 'clubs' | 'spades';
type Rank = 'A' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K';

interface Card {
  suit: Suit;
  rank: Rank;
  faceUp: boolean;
  id: string;
}

interface GameState {
  stock: Card[];
  waste: Card[];
  foundations: { [key in Suit]: Card[] };
  tableau: Card[][];
}

const SUITS: Suit[] = ['hearts', 'diamonds', 'clubs', 'spades'];
const RANKS: Rank[] = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

const SUIT_SYMBOLS: Record<Suit, string> = {
  hearts: '♥',
  diamonds: '♦',
  clubs: '♣',
  spades: '♠',
};

const isRed = (suit: Suit) => suit === 'hearts' || suit === 'diamonds';
const getRankValue = (rank: Rank): number => RANKS.indexOf(rank);

const createDeck = (): Card[] => {
  const deck: Card[] = [];
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      deck.push({
        suit,
        rank,
        faceUp: false,
        id: `${rank}-${suit}`,
      });
    }
  }
  return deck;
};

const shuffleDeck = (deck: Card[]): Card[] => {
  const shuffled = [...deck];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const initializeGame = (): GameState => {
  const deck = shuffleDeck(createDeck());
  const tableau: Card[][] = [[], [], [], [], [], [], []];
  let cardIndex = 0;

  // Deal cards to tableau
  for (let col = 0; col < 7; col++) {
    for (let row = col; row < 7; row++) {
      const card = { ...deck[cardIndex] };
      card.faceUp = row === col; // Only top card is face up
      tableau[row].push(card);
      cardIndex++;
    }
  }

  // Remaining cards go to stock
  const stock = deck.slice(cardIndex).map(card => ({ ...card, faceUp: false }));

  return {
    stock,
    waste: [],
    foundations: {
      hearts: [],
      diamonds: [],
      clubs: [],
      spades: [],
    },
    tableau,
  };
};

export const Solitaire: React.FC = () => {
  const [game, setGame] = useState<GameState>(() => initializeGame());
  const [selectedCard, setSelectedCard] = useState<{ pile: string; index: number } | null>(null);
  const [moves, setMoves] = useState(0);
  const [hasWon, setHasWon] = useState(false);

  const newGame = useCallback(() => {
    setGame(initializeGame());
    setSelectedCard(null);
    setMoves(0);
    setHasWon(false);
  }, []);

  // Check for win
  useEffect(() => {
    const totalInFoundations = SUITS.reduce(
      (sum, suit) => sum + game.foundations[suit].length,
      0
    );
    if (totalInFoundations === 52) {
      setHasWon(true);
    }
  }, [game.foundations]);

  const drawFromStock = useCallback(() => {
    setGame(prev => {
      if (prev.stock.length === 0) {
        // Flip waste back to stock
        return {
          ...prev,
          stock: prev.waste.reverse().map(card => ({ ...card, faceUp: false })),
          waste: [],
        };
      }

      // Draw one card from stock to waste
      const newStock = [...prev.stock];
      const drawnCard = { ...newStock.pop()!, faceUp: true };

      return {
        ...prev,
        stock: newStock,
        waste: [...prev.waste, drawnCard],
      };
    });
    setSelectedCard(null);
  }, []);

  const canMoveToFoundation = (card: Card, foundation: Card[]): boolean => {
    if (foundation.length === 0) {
      return card.rank === 'A';
    }
    const topCard = foundation[foundation.length - 1];
    return (
      card.suit === topCard.suit &&
      getRankValue(card.rank) === getRankValue(topCard.rank) + 1
    );
  };

  const canMoveToTableau = (card: Card, pile: Card[]): boolean => {
    if (pile.length === 0) {
      return card.rank === 'K';
    }
    const topCard = pile[pile.length - 1];
    if (!topCard.faceUp) return false;
    return (
      isRed(card.suit) !== isRed(topCard.suit) &&
      getRankValue(card.rank) === getRankValue(topCard.rank) - 1
    );
  };

  const handleCardClick = useCallback((pile: string, cardIndex: number) => {
    // If clicking the stock pile
    if (pile === 'stock') {
      drawFromStock();
      return;
    }

    // Get the clicked card info
    let clickedCards: Card[] = [];

    if (pile === 'waste') {
      if (game.waste.length === 0) return;
      clickedCards = [game.waste[game.waste.length - 1]];
    } else if (pile.startsWith('foundation-')) {
      const suit = pile.replace('foundation-', '') as Suit;
      if (game.foundations[suit].length === 0) return;
      clickedCards = [game.foundations[suit][game.foundations[suit].length - 1]];
    } else if (pile.startsWith('tableau-')) {
      const colIdx = parseInt(pile.replace('tableau-', ''));
      const column = game.tableau[colIdx];
      if (cardIndex >= column.length || !column[cardIndex].faceUp) {
        // Can't select face-down cards
        if (cardIndex === column.length - 1 && !column[cardIndex].faceUp) {
          // Flip the card if it's the top face-down card
          setGame(prev => {
            const newTableau = prev.tableau.map((col, idx) =>
              idx === colIdx
                ? col.map((card, cIdx) =>
                    cIdx === cardIndex ? { ...card, faceUp: true } : card
                  )
                : col
            );
            return { ...prev, tableau: newTableau };
          });
        }
        return;
      }
      clickedCards = column.slice(cardIndex);
    }

    // If no card is currently selected, select this one
    if (!selectedCard) {
      if (clickedCards.length > 0 && clickedCards[0].faceUp) {
        setSelectedCard({ pile, index: cardIndex });
      }
      return;
    }

    // If clicking the same card, deselect
    if (selectedCard.pile === pile && selectedCard.index === cardIndex) {
      setSelectedCard(null);
      return;
    }

    // Try to move the selected card(s) to the clicked location
    setGame(prev => {
      let newState = { ...prev };
      let sourceCards: Card[] = [];
      let moved = false;

      // Get source cards
      if (selectedCard.pile === 'waste') {
        if (prev.waste.length > 0) {
          sourceCards = [prev.waste[prev.waste.length - 1]];
        }
      } else if (selectedCard.pile.startsWith('foundation-')) {
        const suit = selectedCard.pile.replace('foundation-', '') as Suit;
        if (prev.foundations[suit].length > 0) {
          sourceCards = [prev.foundations[suit][prev.foundations[suit].length - 1]];
        }
      } else if (selectedCard.pile.startsWith('tableau-')) {
        const colIdx = parseInt(selectedCard.pile.replace('tableau-', ''));
        sourceCards = prev.tableau[colIdx].slice(selectedCard.index);
      }

      if (sourceCards.length === 0) {
        return prev;
      }

      // Try to move to foundation
      if (pile.startsWith('foundation-')) {
        const suit = pile.replace('foundation-', '') as Suit;
        if (sourceCards.length === 1 && canMoveToFoundation(sourceCards[0], prev.foundations[suit])) {
          newState.foundations = {
            ...prev.foundations,
            [suit]: [...prev.foundations[suit], sourceCards[0]],
          };
          moved = true;
        }
      }
      // Try to move to tableau
      else if (pile.startsWith('tableau-')) {
        const destColIdx = parseInt(pile.replace('tableau-', ''));
        if (canMoveToTableau(sourceCards[0], prev.tableau[destColIdx])) {
          newState.tableau = prev.tableau.map((col, idx) =>
            idx === destColIdx ? [...col, ...sourceCards] : col
          );
          moved = true;
        }
      }

      if (!moved) {
        return prev;
      }

      // Remove cards from source
      if (selectedCard.pile === 'waste') {
        newState.waste = prev.waste.slice(0, -1);
      } else if (selectedCard.pile.startsWith('foundation-')) {
        const suit = selectedCard.pile.replace('foundation-', '') as Suit;
        newState.foundations = {
          ...newState.foundations,
          [suit]: prev.foundations[suit].slice(0, -1),
        };
      } else if (selectedCard.pile.startsWith('tableau-')) {
        const srcColIdx = parseInt(selectedCard.pile.replace('tableau-', ''));
        newState.tableau = newState.tableau.map((col, idx) =>
          idx === srcColIdx ? prev.tableau[srcColIdx].slice(0, selectedCard.index) : col
        );
      }

      setMoves(m => m + 1);
      return newState;
    });

    setSelectedCard(null);
  }, [game, selectedCard, drawFromStock]);

  // Auto-move to foundation on double-click
  const handleDoubleClick = useCallback((pile: string, cardIndex: number) => {
    let card: Card | null = null;

    if (pile === 'waste' && game.waste.length > 0) {
      card = game.waste[game.waste.length - 1];
    } else if (pile.startsWith('tableau-')) {
      const colIdx = parseInt(pile.replace('tableau-', ''));
      const column = game.tableau[colIdx];
      if (cardIndex === column.length - 1 && column[cardIndex].faceUp) {
        card = column[cardIndex];
      }
    }

    if (!card) return;

    // Try to move to foundation
    if (canMoveToFoundation(card, game.foundations[card.suit])) {
      setGame(prev => {
        const newFoundations = {
          ...prev.foundations,
          [card!.suit]: [...prev.foundations[card!.suit], card!],
        };

        let newState = { ...prev, foundations: newFoundations };

        if (pile === 'waste') {
          newState.waste = prev.waste.slice(0, -1);
        } else {
          const colIdx = parseInt(pile.replace('tableau-', ''));
          newState.tableau = prev.tableau.map((col, idx) =>
            idx === colIdx ? col.slice(0, -1) : col
          );
        }

        return newState;
      });
      setMoves(m => m + 1);
      setSelectedCard(null);
    }
  }, [game]);

  const renderCard = (card: Card | null, pile: string, index: number, stacked = false) => {
    const isSelected =
      selectedCard?.pile === pile &&
      (pile.startsWith('tableau-') ? index >= selectedCard.index : index === selectedCard.index);

    if (!card) {
      return (
        <div
          className={`${styles.card} ${styles.empty}`}
          onClick={() => handleCardClick(pile, index)}
        />
      );
    }

    if (!card.faceUp) {
      return (
        <div
          className={`${styles.card} ${styles.faceDown}`}
          style={stacked ? { marginTop: '-60px' } : undefined}
          onClick={() => handleCardClick(pile, index)}
        />
      );
    }

    return (
      <div
        className={`${styles.card} ${styles.faceUp} ${isRed(card.suit) ? styles.red : styles.black} ${isSelected ? styles.selected : ''}`}
        style={stacked ? { marginTop: card.faceUp ? '-45px' : '-60px' } : undefined}
        onClick={() => handleCardClick(pile, index)}
        onDoubleClick={() => handleDoubleClick(pile, index)}
      >
        <div className={styles.cardCorner}>
          <span className={styles.rank}>{card.rank}</span>
          <span className={styles.suit}>{SUIT_SYMBOLS[card.suit]}</span>
        </div>
        <div className={styles.cardCenter}>
          {SUIT_SYMBOLS[card.suit]}
        </div>
        <div className={`${styles.cardCorner} ${styles.bottomRight}`}>
          <span className={styles.rank}>{card.rank}</span>
          <span className={styles.suit}>{SUIT_SYMBOLS[card.suit]}</span>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.solitaire}>
      {/* Top row: Stock, Waste, Foundations */}
      <div className={styles.topRow}>
        <div className={styles.stockArea}>
          {/* Stock */}
          <div
            className={`${styles.card} ${game.stock.length > 0 ? styles.faceDown : styles.empty} ${styles.stock}`}
            onClick={() => handleCardClick('stock', 0)}
          >
            {game.stock.length === 0 && <span className={styles.refreshIcon}>↺</span>}
          </div>

          {/* Waste */}
          <div className={styles.wastePile}>
            {game.waste.length > 0 ? (
              renderCard(game.waste[game.waste.length - 1], 'waste', game.waste.length - 1)
            ) : (
              <div className={`${styles.card} ${styles.empty}`} />
            )}
          </div>
        </div>

        <div className={styles.foundations}>
          {SUITS.map(suit => (
            <div
              key={suit}
              className={`${styles.card} ${styles.foundation} ${game.foundations[suit].length === 0 ? styles.empty : ''}`}
              onClick={() => handleCardClick(`foundation-${suit}`, game.foundations[suit].length - 1)}
            >
              {game.foundations[suit].length > 0 ? (
                <>
                  <div className={`${styles.cardCorner} ${isRed(suit) ? styles.red : styles.black}`}>
                    <span className={styles.rank}>{game.foundations[suit][game.foundations[suit].length - 1].rank}</span>
                    <span className={styles.suit}>{SUIT_SYMBOLS[suit]}</span>
                  </div>
                  <div className={`${styles.cardCenter} ${isRed(suit) ? styles.red : styles.black}`}>
                    {SUIT_SYMBOLS[suit]}
                  </div>
                </>
              ) : (
                <span className={`${styles.foundationSuit} ${isRed(suit) ? styles.red : styles.black}`}>
                  {SUIT_SYMBOLS[suit]}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Tableau */}
      <div className={styles.tableau}>
        {game.tableau.map((pile, pileIdx) => (
          <div key={pileIdx} className={styles.tableauPile}>
            {pile.length === 0 ? (
              <div
                className={`${styles.card} ${styles.empty}`}
                onClick={() => handleCardClick(`tableau-${pileIdx}`, 0)}
              />
            ) : (
              pile.map((card, cardIdx) => (
                <div key={card.id}>
                  {renderCard(card, `tableau-${pileIdx}`, cardIdx, cardIdx > 0)}
                </div>
              ))
            )}
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className={styles.controls}>
        <button onClick={newGame} className={styles.newGameBtn}>New Game</button>
        <span className={styles.moves}>Moves: {moves}</span>
      </div>

      {/* Win overlay */}
      {hasWon && (
        <div className={styles.winOverlay}>
          <div className={styles.winMessage}>
            <h2>🎉 Congratulations! 🎉</h2>
            <p>You won in {moves} moves!</p>
            <button onClick={newGame} className={styles.playAgainBtn}>Play Again</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Solitaire;
